/**
 * Checkout telemetry — shared allowlist and sanitiser (the client sends these events from
 * src/components/payment/checkoutTelemetry.ts).
 * Nothing the client sends reaches the table except the allowlisted event name, a
 * known pack id, a PayFast uuid-shaped token and a scrubbed free-text detail.
 * amount_zar is always derived server-side from the pack table; user_id is never read
 * from the body.
 */
import { PAYG_PACKS, isPaygPackId } from './paygPacks.js';

export const CHECKOUT_EVENTS = [
  'modal_opened',
  'engine_loaded',
  'callback_true',
  'callback_false',
  'timeout',
  'cancelled_return',
  'rate_limited',
  'coupon_applied',
  'coupon_rejected',
] as const;
export type CheckoutEvent = typeof CHECKOUT_EVENTS[number];

export interface CheckoutEventRow {
  event: CheckoutEvent;
  amount_zar: number | null;
  pack_id: string | null;
  payfast_uuid: string | null;
  detail: string | null;
}

export type SanitisedCheckoutEvent =
  | { ok: true; row: CheckoutEventRow }
  | { ok: false; error: string };

const PAYFAST_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DETAIL_DISALLOWED_RE = /[^A-Za-z0-9 _:,-]/g;
// 7+ digits, optionally separated by spaces, dots or dashes (phone/ID/card-like runs).
const DETAIL_DIGITS_RE = /\d(?:[ .-]*\d){6,}/g;
const DETAIL_MAX = 200;

function present(v: unknown): boolean {
  return v !== undefined && v !== null;
}

export function sanitiseCheckoutEvent(body: unknown): SanitisedCheckoutEvent {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return { ok: false, error: 'Body must be a JSON object' };
  }
  const b = body as Record<string, unknown>;

  if (typeof b.event !== 'string' || !(CHECKOUT_EVENTS as readonly string[]).includes(b.event)) {
    return { ok: false, error: 'Unknown event' };
  }
  const event = b.event as CheckoutEvent;

  let packId: string | null = null;
  let amount: number | null = null;
  if (present(b.pack_id)) {
    if (!isPaygPackId(b.pack_id)) return { ok: false, error: 'Unknown pack_id' };
    packId = b.pack_id;
    amount = PAYG_PACKS[b.pack_id].priceZar;
  }

  let payfastUuid: string | null = null;
  if (present(b.payfast_uuid)) {
    if (typeof b.payfast_uuid !== 'string' || !PAYFAST_UUID_RE.test(b.payfast_uuid)) {
      return { ok: false, error: 'Invalid payfast_uuid' };
    }
    payfastUuid = b.payfast_uuid;
  }

  let detail: string | null = null;
  if (typeof b.detail === 'string') {
    const cleaned = b.detail
      .replace(DETAIL_DISALLOWED_RE, ' ')
      .replace(DETAIL_DIGITS_RE, '[n]')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, DETAIL_MAX)
      .trim();
    detail = cleaned || null;
  }

  return { ok: true, row: { event, amount_zar: amount, pack_id: packId, payfast_uuid: payfastUuid, detail } };
}
